import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, Check } from "lucide-react";
import PasswordStrength, { evaluatePassword } from "@/components/PasswordStrength";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] px-6" data-testid="reset-password-missing-token">
        <div className="max-w-md text-center">
          <div className="eyebrow mb-2">Invalid link</div>
          <h1 className="aperture-h1 text-3xl">Missing reset token.</h1>
          <p className="mt-3 text-sm text-navy/60">Please request a new password reset link.</p>
          <Link to="/forgot-password" className="mt-6 inline-block btn-primary px-6 py-3 text-xs uppercase tracking-widest rounded-sm">Request new link</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    if (!evaluatePassword(next).rulesPassed) { toast.error("Password does not meet strength requirements"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      setTimeout(() => nav("/login"), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. The link may have expired.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] px-6" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-full border-2 border-gold flex items-center justify-center mb-4">
            {done ? <Check className="w-6 h-6 text-gold" strokeWidth={2} /> : <KeyRound className="w-6 h-6 text-gold" strokeWidth={1.5} />}
          </div>
          <div className="eyebrow">Account recovery</div>
          <h1 className="aperture-h1 text-3xl mt-2">{done ? "Password updated." : "Set a new password."}</h1>
          {done && <p className="mt-3 text-sm text-navy/60">Redirecting to sign-in…</p>}
        </div>

        {!done && (
          <form onSubmit={submit} className="bg-white p-8 shadow-xl rounded-sm space-y-4">
            <div>
              <label className="eyebrow block mb-2">New password</label>
              <input required type="password" value={next} onChange={(e)=>setNext(e.target.value)}
                data-testid="reset-password-new"
                className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
              <PasswordStrength password={next} />
            </div>
            <div>
              <label className="eyebrow block mb-2">Confirm new password</label>
              <input required type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)}
                data-testid="reset-password-confirm"
                className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            </div>
            <button disabled={busy} data-testid="reset-password-submit" className="btn-primary w-full py-3 text-xs uppercase tracking-widest rounded-sm">
              {busy ? "Updating…" : "Update password"}
            </button>
            <Link to="/login" className="mt-2 flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-gold transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
